import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Paper,
  Avatar,
  Rating,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
  Fade,
  Grow,
  Stack
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import CodeIcon from "@mui/icons-material/Code";
import DevicesIcon from "@mui/icons-material/Devices";
import StorageIcon from "@mui/icons-material/Storage";
import CloudIcon from "@mui/icons-material/Cloud";
import SecurityIcon from "@mui/icons-material/Security";

// Import images properly
import portfolio1 from "../../assets/images/p1.png";
import portfolio2 from "../../assets/images/p1.png";
import portfolio3 from "../../assets/images/p1.png";
import portfolio4 from "../../assets/images/p1.png";
import portfolio5 from "../../assets/images/p1.png";
import portfolio6 from "../../assets/images/p1.png";

// Animation keyframes
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const glow = keyframes`
  0% { box-shadow: 0 0 5px rgba(33, 150, 243, 0.2); }
  50% { box-shadow: 0 0 20px rgba(33, 150, 243, 0.4); }
  100% { box-shadow: 0 0 5px rgba(33, 150, 243, 0.2); }
`;

// Styled Components
const SectionWrapper = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.grey[50]} 100%)`,
  padding: theme.spacing(15, 0),
  position: "relative",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(8, 0),
  },
}));

const LaptopFrame = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  maxWidth: "600px",
  margin: "0 auto",
  animation: `${float} 6s ease-in-out infinite`,
  "&::before": {
    content: '""',
    display: "block",
    paddingTop: "62.5%", // 16:10 aspect ratio for laptop
  },
}));

const LaptopScreen = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "5%",
  left: "12%",
  right: "12%",
  bottom: "15%",
  background: theme.palette.background.paper,
  borderRadius: "8px",
  overflow: "hidden",
  boxShadow: "inset 0 0 20px rgba(0,0,0,0.1)",
  border: `2px solid ${theme.palette.grey[300]}`,
}));

const LaptopBody = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: `linear-gradient(145deg, ${theme.palette.grey[700]}, ${theme.palette.grey[900]})`,
  borderRadius: "20px",
  boxShadow: "0 30px 40px -20px rgba(0,0,0,0.5)",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "60px",
    height: "4px",
    background: theme.palette.grey[600],
    borderRadius: "2px",
    bottom: "5%",
  },
}));

const PortfolioImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  transition: "transform 0.6s ease",
});

const TestimonialCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  background: theme.palette.background.paper,
  boxShadow: "0 20px 40px -15px rgba(0,0,0,0.1)",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: "0 30px 50px -20px rgba(0,0,0,0.15)",
  },
}));

const CompanyLogo = styled(Box)(({ theme }) => ({
  width: 60,
  height: 60,
  borderRadius: "16px",
  background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "white",
  fontSize: "2rem",
  fontWeight: 700,
  marginBottom: theme.spacing(2),
}));

const TechChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  background: theme.palette.grey[100],
  "&:hover": {
    background: theme.palette.primary.light,
    color: "white",
  },
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  background: theme.palette.background.paper,
  boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
  "&:hover": {
    background: theme.palette.primary.main,
    color: "white",
    transform: "scale(1.1)",
  },
  transition: "all 0.3s ease",
}));

// Testimonials Data - Tech Company Related
const testimonials = [
  {
    id: 1,
    name: "Rajesh Kumar",
    position: "CTO",
    company: "TechSolutions Inc.",
    avatar: "RK",
    rating: 5,
    text: "4Techbugs Technologies transformed our entire digital infrastructure. Their cloud migration strategy reduced our operational costs by 40% while improving performance. The team's expertise in microservices architecture is outstanding!",
    technologies: ["AWS", "Docker", "Kubernetes"],
    icon: <CloudIcon />,
    linkedin: "#",
    twitter: "#",
  },
  {
    id: 2,
    name: "Priya Sharma",
    position: "Product Manager",
    company: "InnovateLabs",
    avatar: "PS",
    rating: 5,
    text: "The mobile app developed by 4Techbugs exceeded our expectations. They didn't just code; they provided valuable insights on UX that increased our user retention by 60%. Truly a partner in innovation!",
    technologies: ["React Native", "Firebase", "AI/ML"],
    icon: <DevicesIcon />,
    linkedin: "#",
    twitter: "#",
  },
  {
    id: 3,
    name: "Amit Patel",
    position: "Lead Architect",
    company: "DataFlow Systems",
    avatar: "AP",
    rating: 5,
    text: "Working with 4Techbugs on our big data platform was a game-changer. They implemented real-time processing pipelines that handle millions of transactions daily. Their attention to security and scalability is impressive.",
    technologies: ["Apache Kafka", "MongoDB", "Python"],
    icon: <StorageIcon />,
    linkedin: "#",
    twitter: "#",
  },
  {
    id: 4,
    name: "Neha Gupta",
    position: "CEO",
    company: "WebWizards",
    avatar: "NG",
    rating: 5,
    text: "4Techbugs redesigned our corporate website and the results were immediate. Our bounce rate dropped by 50% and conversions increased by 80%. They understand modern web development perfectly!",
    technologies: ["React", "Node.js", "GraphQL"],
    icon: <CodeIcon />,
    linkedin: "#",
    twitter: "#",
  },
];

const PortfolioSection = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [hoveredImage, setHoveredImage] = useState(null);

  const portfolioImages = [
    { src: portfolio1, title: "E-Commerce Platform", category: "Web App" },
    { src: portfolio2, title: "Mobile Banking App", category: "Mobile" },
    { src: portfolio3, title: "Dashboard Design", category: "UI/UX" },
    { src: portfolio4, title: "Social Media Platform", category: "Web App" },
    { src: portfolio5, title: "Portfolio Website", category: "Website" },
    { src: portfolio6, title: "Admin Template", category: "UI/UX" },
  ];

  const handleNextTestimonial = () => {
    setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const handlePrevTestimonial = () => {
    setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const handleSeePortfolio = () => {
    navigate("/portfolio");
  };

  const handlePortfolioItemClick = (title) => {
    navigate(`/portfolio/${title.toLowerCase().replace(/\s+/g, '-')}`);
  };

  return (
    <SectionWrapper>
      <Container maxWidth="xl">
        <Grid container spacing={isMobile ? 4 : 8} alignItems="center">
          {/* Left Side - Portfolio with Laptop Frame */}
          <Grid item xs={12} md={6}>
            <Fade in timeout={1000}>
              <Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "2rem", md: "2.5rem" },
                    fontWeight: 700,
                    mb: 3,
                    background: `linear-gradient(135deg, ${theme.palette.text.primary}, ${theme.palette.primary.main})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Our Portfolio
                </Typography>

                <LaptopFrame>
                  <LaptopBody />
                  <LaptopScreen>
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        height: "100%",
                        cursor: "pointer",
                        "&:hover img": {
                          transform: "scale(1.05)",
                        },
                      }}
                      onClick={() => handlePortfolioItemClick(portfolioImages[0].title)}
                    >
                      <PortfolioImage
                        src={portfolioImages[0].src}
                        alt="Featured Project"
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          background: "linear-gradient(transparent, rgba(0,0,0,0.8))",
                          color: "white",
                          p: 2,
                          transform: "translateY(100%)",
                          transition: "transform 0.3s ease",
                          ".MuiLaptopScreen:hover &": {
                            transform: "translateY(0)",
                          },
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {portfolioImages[0].title}
                        </Typography>
                        <Typography variant="caption">
                          {portfolioImages[0].category}
                        </Typography>
                      </Box>
                    </Box>
                  </LaptopScreen>
                </LaptopFrame>

                <Box sx={{ mt: 4, textAlign: "center" }}>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    We've designed awesome experiences for tech companies worldwide
                  </Typography>
                  
                  <Button
                    variant="contained"
                    onClick={handleSeePortfolio}
                    sx={{
                      py: 1.5,
                      px: 4,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      borderRadius: 3,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      boxShadow: `0 10px 20px -5px ${theme.palette.primary.main}80`,
                      "&:hover": {
                        transform: "translateY(-3px)",
                        boxShadow: `0 20px 30px -8px ${theme.palette.primary.main}`,
                      },
                    }}
                  >
                    <BusinessCenterIcon sx={{ mr: 1 }} />
                    SEE FULL PORTFOLIO
                  </Button>
                </Box>
              </Box>
            </Fade>
          </Grid>

          {/* Right Side - Tech Company Testimonials */}
          <Grid item xs={12} md={6}>
            <Fade in timeout={1000} style={{ transitionDelay: "200ms" }}>
              <Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "2rem", md: "2.5rem" },
                    fontWeight: 700,
                    mb: 3,
                    textAlign: { xs: "center", md: "left" },
                    background: `linear-gradient(135deg, ${theme.palette.text.primary}, ${theme.palette.secondary.main})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  What Tech Leaders Say
                </Typography>

                <Box sx={{ position: "relative", px: { xs: 2, md: 0 } }}>
                  {/* Navigation Buttons */}
                  {!isMobile && (
                    <>
                      <NavigationButton
                        onClick={handlePrevTestimonial}
                        sx={{ position: "absolute", left: -20, top: "50%", transform: "translateY(-50%)", zIndex: 2 }}
                      >
                        <ChevronLeftIcon />
                      </NavigationButton>
                      <NavigationButton
                        onClick={handleNextTestimonial}
                        sx={{ position: "absolute", right: -20, top: "50%", transform: "translateY(-50%)", zIndex: 2 }}
                      >
                        <ChevronRightIcon />
                      </NavigationButton>
                    </>
                  )}

                  {/* Testimonial Cards Grid */}
                  <Grid container spacing={3}>
                    {testimonials.map((testimonial, index) => (
                      <Grid item xs={12} sm={6} key={testimonial.id}>
                        <Grow
                          in
                          timeout={500 + index * 100}
                          style={{ transformOrigin: "0 0 0" }}
                        >
                          <TestimonialCard elevation={0}>
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
                              <Avatar
                                sx={{
                                  width: 56,
                                  height: 56,
                                  bgcolor: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                  fontSize: "1.2rem",
                                  fontWeight: 600,
                                  animation: `${glow} 3s ease-in-out infinite`,
                                }}
                              >
                                {testimonial.avatar}
                              </Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  {testimonial.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {testimonial.position} at {testimonial.company}
                                </Typography>
                                <Rating value={testimonial.rating} readOnly size="small" sx={{ mt: 0.5 }} />
                              </Box>
                            </Box>

                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mb: 2,
                                lineHeight: 1.8,
                                flex: 1,
                                fontStyle: "italic",
                              }}
                            >
                              "{testimonial.text}"
                            </Typography>

                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" color="primary" sx={{ fontWeight: 600, display: "block", mb: 1 }}>
                Technologies Used:
                              </Typography>
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {testimonial.technologies.map((tech, i) => (
                                  <TechChip
                                    key={i}
                                    label={tech}
                                    size="small"
                                    icon={testimonial.icon}
                                  />
                                ))}
                              </Box>
                            </Box>

                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <Chip
                                icon={<BusinessCenterIcon />}
                                label={testimonial.company}
                                size="small"
                                variant="outlined"
                                color="primary"
                              />
                              <Box>
                                <IconButton size="small" href={testimonial.linkedin} sx={{ color: "#0077b5" }}>
                                  <LinkedInIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" href={testimonial.twitter} sx={{ color: "#1DA1F2" }}>
                                  <TwitterIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          </TestimonialCard>
                        </Grow>
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                {/* Mobile Navigation Dots */}
                {isMobile && (
                  <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 3 }}>
                    {testimonials.map((_, index) => (
                      <Box
                        key={index}
                        onClick={() => setActiveTestimonial(index)}
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: index === activeTestimonial ? "primary.main" : "grey.300",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            </Fade>
          </Grid>
        </Grid>
      </Container>
    </SectionWrapper>
  );
};

export default PortfolioSection;